import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, ExternalLink, Plus, Handshake } from "lucide-react";

const INR = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

const STATUS_COLORS = {
  draft: "bg-zinc-100 text-zinc-700",
  under_review: "bg-blue-100 text-blue-800",
  approved_internal: "bg-emerald-100 text-emerald-800",
  approved: "bg-emerald-100 text-emerald-800",
  converted: "bg-purple-100 text-purple-800",
  sent: "bg-blue-100 text-blue-800",
  negotiation: "bg-amber-100 text-amber-800",
  rejected: "bg-red-100 text-red-800",
  initiated: "bg-amber-100 text-amber-800",
  clarification_requested: "bg-orange-100 text-orange-800",
  accepted: "bg-emerald-100 text-emerald-800",
  planning: "bg-zinc-100 text-zinc-700",
  in_production: "bg-blue-100 text-blue-800",
  paid: "bg-emerald-100 text-emerald-800",
  partial: "bg-amber-100 text-amber-800",
  overdue: "bg-red-100 text-red-800",
};

function Pill({ status }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-widest ${STATUS_COLORS[status] || "bg-zinc-100 text-zinc-700"}`}>
      {(status || "").replace(/_/g, " ")}
    </span>
  );
}

export default function ClientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState("overview");

  const { data, isLoading } = useQuery({
    queryKey: ["client", id],
    queryFn: async () => (await api.get(`/clients/${id}`)).data,
  });
  const { data: estimations = [] } = useQuery({
    queryKey: ["client-estimations", id],
    queryFn: async () => (await api.get(`/estimations`, { params: { client_id: id } })).data,
  });
  const { data: handovers = [] } = useQuery({
    queryKey: ["client-handovers", id],
    queryFn: async () => (await api.get(`/handovers`)).data.then ? [] : (await api.get(`/handovers`)).data,
    // simpler:
  });
  const { data: allHandovers = [] } = useQuery({
    queryKey: ["all-handovers"],
    queryFn: async () => (await api.get(`/handovers`)).data,
  });
  const clientHandovers = allHandovers.filter((h) => h.client_id === id);
  const { data: invoices = [] } = useQuery({
    queryKey: ["client-invoices", id],
    queryFn: async () => (await api.get(`/invoices`, { params: { client_id: id } })).data,
  });

  const initiateHandoverMut = useMutation({
    mutationFn: (quotation_id) => api.post("/handovers", { client_id: id, quotation_id }),
    onSuccess: (r) => {
      toast.success(`Handover ${r.data.number} initiated · Ops Manager notified`);
      qc.invalidateQueries({ queryKey: ["all-handovers"] });
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  if (isLoading || !data) return <div className="p-12 text-sm text-zinc-500 font-mono">Loading Client 360°…</div>;
  const { client, projects, quotations } = data;

  const stats = {
    revenue: invoices.filter((i) => i.status === "paid").reduce((s, i) => s + (i.total || 0), 0),
    outstanding: invoices.reduce((s, i) => s + (i.balance_due || 0), 0),
    activeProjects: projects.filter((p) => ["planning", "in_production", "qa"].includes(p.status)).length,
  };

  return (
    <div data-testid="client-detail">
      <PageHeader
        eyebrow={<Link to="/app/clients" className="hover:underline inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> All Clients</Link>}
        title={<span className="flex items-center gap-3">{client.name} <span className="text-[10px] font-semibold uppercase tracking-[0.16em] bg-zinc-900 text-white rounded px-2 py-1">Client 360°</span></span>}
        description={<span>{client.legal_name || client.industry || "—"} · <span className="capitalize font-mono text-xs">{client.billing_model.replace("_", " ")}</span>{client.hourly_rate ? ` · ₹${client.hourly_rate}/hr` : ""}</span>}
      />
      <div className="max-w-[1400px] mx-auto px-8 py-6">
        <div className="grid md:grid-cols-4 gap-4 mb-6" data-testid="client-360-stats">
          <div className="border border-zinc-200 rounded-lg bg-white p-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Revenue (paid)</div>
            <div className="mt-1 font-heading text-2xl font-extrabold text-emerald-700">{INR(stats.revenue)}</div>
          </div>
          <div className="border border-zinc-200 rounded-lg bg-white p-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Outstanding</div>
            <div className="mt-1 font-heading text-2xl font-extrabold text-amber-700">{INR(stats.outstanding)}</div>
          </div>
          <div className="border border-zinc-200 rounded-lg bg-white p-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Active Projects</div>
            <div className="mt-1 font-heading text-2xl font-extrabold">{stats.activeProjects}</div>
          </div>
          <div className="border border-zinc-200 rounded-lg bg-white p-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Country / Currency</div>
            <div className="mt-1 font-heading text-lg font-bold">{client.country} · {client.currency}</div>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="client-tabs" className="flex-wrap h-auto">
            <TabsTrigger value="overview" data-testid="client-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="client-tab-contacts">Contacts ({client.contacts?.length || 0})</TabsTrigger>
            <TabsTrigger value="estimations" data-testid="client-tab-estimations">Estimations ({estimations.length})</TabsTrigger>
            <TabsTrigger value="quotations" data-testid="client-tab-quotations">Quotations ({quotations.length})</TabsTrigger>
            <TabsTrigger value="handovers" data-testid="client-tab-handovers">Handovers ({clientHandovers.length})</TabsTrigger>
            <TabsTrigger value="projects" data-testid="client-tab-projects">Projects ({projects.length})</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="client-tab-invoices">Invoices ({invoices.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4 space-y-4">
            <div className="border border-zinc-200 rounded-lg bg-white p-6">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Client Notes</div>
              <div className="mt-2 text-sm text-zinc-700 leading-relaxed">{client.notes || "No notes on file."}</div>
            </div>
            <div className="border border-zinc-200 rounded-lg bg-white p-6">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Quick actions</div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" onClick={() => nav(`/app/estimations?client=${id}&new=1`)} data-testid="qa-new-estimation">
                  <Plus className="w-3.5 h-3.5 mr-1" /> New Estimation
                </Button>
                <Button size="sm" variant="outline" onClick={() => nav(`/app/quotations?client=${id}`)} data-testid="qa-view-quotations">
                  View Quotations
                </Button>
                <Button size="sm" variant="outline" onClick={() => nav(`/app/invoices?client=${id}&new_monthly=1`)} data-testid="qa-monthly-invoice">
                  Monthly Invoice from Hours
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="contacts" className="mt-4">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {(client.contacts || []).map((c, i) => (
                <div key={`${c.email || c.name || "contact"}-${i}`} className="border border-zinc-200 rounded-lg bg-white p-4">
                  <div className="font-medium">{c.name}</div>
                  <div className="text-[11px] text-zinc-500">{c.role || "—"}</div>
                  <div className="text-xs text-zinc-600 mt-2 font-mono">{c.email || ""}</div>
                  <div className="text-xs text-zinc-600 font-mono">{c.phone || ""}</div>
                </div>
              ))}
              {(client.contacts || []).length === 0 && <div className="text-sm text-zinc-500">No contacts on file.</div>}
            </div>
          </TabsContent>

          <TabsContent value="estimations" className="mt-4">
            <div className="border border-zinc-200 rounded-lg bg-white overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-200"><tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500">
                  <th className="px-4 py-3">Number</th><th className="px-4 py-3">Title</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Hours</th><th className="px-4 py-3 text-right">Suggested Price</th><th></th>
                </tr></thead>
                <tbody className="divide-y divide-zinc-100">
                  {estimations.map((e) => (
                    <tr key={e.id}><td className="px-4 py-3 font-mono text-xs">{e.number}</td>
                      <td className="px-4 py-3 font-medium">{e.title}</td>
                      <td className="px-4 py-3"><Pill status={e.status} /></td>
                      <td className="px-4 py-3 text-right font-mono">{e.total_hours}</td>
                      <td className="px-4 py-3 text-right font-mono">{INR(e.suggested_client_price)}</td>
                      <td className="px-4 py-3 text-right"><Link to={`/app/estimations/${e.id}`} className="text-xs inline-flex items-center gap-1">Open <ExternalLink className="w-3 h-3" /></Link></td>
                    </tr>
                  ))}
                  {estimations.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-zinc-500">No estimations yet. Create one from Quick Actions.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="quotations" className="mt-4">
            <div className="border border-zinc-200 rounded-lg bg-white overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-200"><tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500">
                  <th className="px-4 py-3">Number</th><th className="px-4 py-3">Title</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Total</th><th></th>
                </tr></thead>
                <tbody className="divide-y divide-zinc-100">
                  {quotations.map((q) => (
                    <tr key={q.id}><td className="px-4 py-3 font-mono text-xs">{q.number}</td>
                      <td className="px-4 py-3 font-medium">{q.title}</td>
                      <td className="px-4 py-3"><Pill status={q.status} /></td>
                      <td className="px-4 py-3 text-right font-mono">{INR(q.total)}</td>
                      <td className="px-4 py-3 text-right">
                        {q.status === "approved" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => initiateHandoverMut.mutate(q.id)}
                            data-testid={`initiate-handover-${q.id}`}
                          >
                            <Handshake className="w-3 h-3 mr-1" /> Initiate Handover
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {quotations.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-zinc-500">No quotations yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="handovers" className="mt-4">
            <div className="border border-zinc-200 rounded-lg bg-white overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-200"><tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500">
                  <th className="px-4 py-3">Number</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Initiated</th><th className="px-4 py-3">Accepted</th><th></th>
                </tr></thead>
                <tbody className="divide-y divide-zinc-100">
                  {clientHandovers.map((h) => (
                    <tr key={h.id}><td className="px-4 py-3 font-mono text-xs">{h.number}</td>
                      <td className="px-4 py-3"><Pill status={h.status} /></td>
                      <td className="px-4 py-3 text-zinc-600 text-xs">{h.initiated_at?.slice(0, 10)}</td>
                      <td className="px-4 py-3 text-zinc-600 text-xs">{h.accepted_at?.slice(0, 10) || "—"}</td>
                      <td className="px-4 py-3 text-right"><Link to={`/app/handovers/${h.id}`} className="text-xs inline-flex items-center gap-1">Open <ExternalLink className="w-3 h-3" /></Link></td>
                    </tr>
                  ))}
                  {clientHandovers.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-zinc-500">No handovers yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="projects" className="mt-4">
            <div className="border border-zinc-200 rounded-lg bg-white overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-200"><tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500">
                  <th className="px-4 py-3">Code</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Billing</th><th className="px-4 py-3 text-right">Budget</th><th></th>
                </tr></thead>
                <tbody className="divide-y divide-zinc-100">
                  {projects.map((p) => (
                    <tr key={p.id}><td className="px-4 py-3 font-mono text-xs">{p.code}</td>
                      <td className="px-4 py-3 font-medium">{p.name}</td>
                      <td className="px-4 py-3"><Pill status={p.status} /></td>
                      <td className="px-4 py-3 capitalize text-zinc-600">{p.billing_model.replace("_", " ")}</td>
                      <td className="px-4 py-3 text-right font-mono">{INR(p.budget)}</td>
                      <td className="px-4 py-3 text-right"><Link to={`/app/projects/${p.id}`} className="text-xs inline-flex items-center gap-1">Open <ExternalLink className="w-3 h-3" /></Link></td>
                    </tr>
                  ))}
                  {projects.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-zinc-500">No projects for this client yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="invoices" className="mt-4">
            <div className="border border-zinc-200 rounded-lg bg-white overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-200"><tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500">
                  <th className="px-4 py-3">Number</th><th className="px-4 py-3">Period</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Total</th><th className="px-4 py-3 text-right">Balance</th><th></th>
                </tr></thead>
                <tbody className="divide-y divide-zinc-100">
                  {invoices.map((inv) => (
                    <tr key={inv.id}><td className="px-4 py-3 font-mono text-xs">{inv.number}</td>
                      <td className="px-4 py-3 font-mono text-xs">{inv.period_month || "—"}</td>
                      <td className="px-4 py-3"><Pill status={inv.status} /></td>
                      <td className="px-4 py-3 text-right font-mono">{INR(inv.total)}</td>
                      <td className="px-4 py-3 text-right font-mono">{INR(inv.balance_due)}</td>
                      <td className="px-4 py-3 text-right"><Link to={`/app/invoices/${inv.id}`} className="text-xs inline-flex items-center gap-1">Open <ExternalLink className="w-3 h-3" /></Link></td>
                    </tr>
                  ))}
                  {invoices.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-zinc-500">No invoices yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
